/**
 * PmLens.jsx - Tracking > Summary, the project-management view: the tracking
 * master's Customer Summary and the burn.
 *   - progress: hours used of the budget, As sold of the budget, the planned
 *     time gone by (the budget's first..last day), weekly burn (MASTER S4)
 *     and the forecast - at that burn, when the budget runs out
 *   - budget / actual / remaining NT, OT, DT, expenses with bars
 *   - by task and by engineer: hours, sold (cost for the cost area)
 *   - the latest entries; the daily grid (Customer Summary rows 10+)
 * The figures are the engine's; the forecast only divides them.
 */
import { useState } from 'react';
import { MUTED, Section, td } from '../quotes/quoteUi';
import { hours, money, pct } from '../costs/costApi';
import { forecastOf, progressOf, recentOf, shortDate, dowOf } from './trackingInsights';
import { Bar, Empty, Head, Hint, Num, Stat } from './trackingUi';
import { linkBtn } from './FinanceLens';

const cell = { ...td, fontSize: 12 };
const tbl = { width: '100%', borderCollapse: 'collapse' };

export default function PmLens({ data, seesCosts, go, today }) {
  const t = data.tracking;
  const T = t.totals;
  const cs = t.customerSummary;
  const P = progressOf(data, today);
  const F = forecastOf(data, today);
  const recent = recentOf(data, 8);
  const [daily, setDaily] = useState(false);
  const costs = !!seesCosts;
  const names = (data.people || []).map((p) => p.name);
  const lastDay = (name) => {
    const mine = t.rows
      .filter((r) => r.person.toLowerCase() === name.toLowerCase())
      .map((r) => r.date)
      .sort();
    return mine.length ? mine[mine.length - 1] : null;
  };

  const forecastText = {
    nobudget: 'Set a budget (Setup > Budget) to forecast when it runs out.',
    noburn: 'No burn yet - log some days first.',
    over: `As sold is already ${money(-(F.left || 0))} past the budget.`,
    ok:
      F.state === 'ok'
        ? `${money(F.left)} of budget left lasts about ${F.weeks < 1 ? 'less than a week' : `${Math.round(F.weeks * 10) / 10} weeks`}.${F.runsOutBeforeEnd ? ` That is before the planned end (${shortDate(F.planEnd, true)}).` : ''}`
        : '',
  }[F.state];

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <Stat
          big
          label="Hours used"
          value={P.budgetHours ? pct(P.hoursPct) : hours(P.usedHours)}
          accent={P.hoursPct > 1 ? '#B71C1C' : undefined}
          note={
            P.budgetHours
              ? `${hours(P.usedHours)} of ${hours(P.budgetHours)} budget hours.`
              : `${hours(P.usedHours)} hours logged - no hours budget.`
          }
          tip="Hours logged (NT + OT + DT) against the budget's hours (Customer Summary L3:M5)."
          from="Log time & expenses; Setup > Budget"
        />
        <Stat
          big
          label="Budget used"
          value={P.soldPct === null ? '-' : pct(P.soldPct)}
          accent={P.soldPct > 1 ? '#B71C1C' : undefined}
          note={P.budgetSold ? `${money(P.soldNow)} As sold of ${money(P.budgetSold)}.` : 'No budget yet.'}
          tip="As sold so far against the budget's sold (or the Quoted figure without a budget)."
          from="Log time & expenses, Materials; Setup > Budget"
        />
        <Stat
          big
          label="Time elapsed"
          value={P.timePct === null ? '-' : pct(P.timePct)}
          note={P.window ? `Planned ${shortDate(P.window.start)} - ${shortDate(P.window.end, true)}.` : 'The budget has no planned days.'}
          tip="Days gone by of the budget's planned days (its first to its last day with hours). When Budget used runs ahead of Time elapsed, the job is burning faster than planned."
          from="Setup > Budget (the worksheet days)"
        />
        <Stat
          big
          label="Weekly pace"
          value={money(F.burn)}
          note={
            F.pace && F.pace.from
              ? `Sold per week, ${shortDate(F.pace.from)} - ${shortDate(F.pace.to)}. Full-crew rate (workbook): ${money(F.fullCrew)}/wk.`
              : 'No days logged yet.'
          }
          tip="What the job actually sold per week lately: labour plus billed expenses (with the handling fee) logged in the last 4 weeks, divided by 4 (fewer weeks on a young job). The workbook's weekly burn (MASTER S4) is shown for reference - it assumes everyone who ever worked the job is on site 7 days a week, so it runs much higher than the real pace."
          from="Log time & expenses"
        />
        <Stat
          big
          label="Budget runs out"
          value={F.state === 'ok' ? `~${shortDate(F.date, true)}` : F.state === 'over' ? 'Already over' : '-'}
          accent={F.state === 'over' || F.runsOutBeforeEnd ? '#B71C1C' : undefined}
          note={forecastText}
          tip="The budget left (budget sold - As sold) divided by the weekly pace, counted from today. A forecast, not a promise: it assumes the last 4 weeks' pace carries on."
          from="Budget used and Weekly pace"
        />
      </div>

      <Section
        title={
          <>
            Budget against actual{' '}
            <Hint
              title="Budget against actual"
              tip="The tracking sheet's Customer Summary: budget hours and expenses, what is logged and what remains. Expenses before the handling fee. The black mark on each bar is the time elapsed."
              from="Setup > Budget (budget); Log time & expenses (actual)"
            />
          </>
        }
      >
        {!(P.budgetHours || cs.budget.expenses) && !T.hours ? (
          <Empty text="No budget and no hours yet." action="Set the budget" onAction={() => go('setup:budget')} />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table aria-label="Budget against actual hours" style={tbl}>
              <Head cols={['', 'Budget', 'Actual', 'Remaining', 'Used']} />
              <tbody>
                {[
                  ['NT', 'nt', hours],
                  ['OT', 'ot', hours],
                  ['DT', 'dt', hours],
                  ['Expenses', 'expenses', money],
                ].map(([label, k, f]) => {
                  const b = cs.budget[k];
                  const a = cs.actual[k];
                  return (
                    <tr key={k}>
                      <td style={{ ...cell, fontWeight: 700 }}>{label}</td>
                      <Num v={f(b)} />
                      <Num v={f(a)} />
                      <Num v={f(cs.remaining[k])} color={cs.remaining[k] < 0 ? '#B71C1C' : undefined} />
                      <td style={{ ...td, width: '32%', minWidth: 120 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1 }}>
                            <Bar
                              share={b ? a / b : a ? 2 : 0}
                              marker={P.timePct === null ? undefined : P.timePct}
                              label={`${label} used`}
                            />
                          </div>
                          <span style={{ fontSize: 10.5, color: MUTED, width: 52, textAlign: 'right' }}>
                            {b ? pct(a / b) : a ? 'no budget' : ''}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
        <Section
          title={
            <>
              By task{' '}
              <Hint
                title="By task"
                tip="Hours and sold by the tracking sheet's tasks, and each task's share of the labour sold."
                from="Log time & expenses"
              />
            </>
          }
        >
          {!T.hours ? (
            <Empty text="No hours logged yet." action="Log time & expenses" onAction={() => go('log')} />
          ) : (
            <table aria-label="Hours by task" style={tbl}>
              <Head cols={['Task', 'Hours', 'Sold', ...(costs ? ['Cost'] : []), 'Share']} />
              <tbody>
                {t.tasks
                  .filter((x) => x.hours)
                  .map((x) => (
                    <tr key={x.task}>
                      <td style={{ ...cell, fontWeight: 600 }}>{x.task}</td>
                      <Num v={hours(x.hours)} />
                      <Num v={money(x.sell)} />
                      {costs && <Num v={money(x.cost)} />}
                      <td style={{ ...td, width: 90 }}>
                        <Bar share={x.share} label={`${x.task} share`} />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </Section>
        <Section
          title={
            <>
              By engineer{' '}
              <Hint
                title="By engineer (hours)"
                tip="Each person's hours, days and sold, and the last day they logged."
                from="Log time & expenses"
              />
            </>
          }
        >
          {!t.people.length ? (
            <Empty text="Nobody on this job yet." action="Add people in Setup" onAction={() => go('setup:people')} />
          ) : (
            <table aria-label="Hours by engineer" style={tbl}>
              <Head cols={['Engineer', 'Hours', 'Days', 'Sold', ...(costs ? ['Cost'] : []), 'Last day']} />
              <tbody>
                {t.people.map((p) => (
                  <tr key={p.name}>
                    <td style={{ ...cell, fontWeight: 600 }}>{p.name}</td>
                    <Num v={hours(p.hours)} />
                    <Num v={p.days} />
                    <Num v={money(p.sell)} />
                    {costs && <Num v={money(p.laborCost)} />}
                    <Num v={lastDay(p.name) ? shortDate(lastDay(p.name)) : '-'} color={MUTED} />
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      </div>

      <Section
        title={
          <>
            Latest entries <Hint title="Latest entries" tip="The last days logged on this job, newest first." from="Log time & expenses" />
          </>
        }
        right={
          <button type="button" onClick={() => go('log')} style={linkBtn}>
            Log time &amp; expenses &rarr;
          </button>
        }
      >
        {!recent.length ? (
          <Empty text="No hours logged yet." action="Log time & expenses" onAction={() => go('log')} />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table aria-label="Latest entries" style={tbl}>
              <Head cols={['Day', 'Engineer', 'Task', 'PO line', 'Hours', 'Expenses', 'Sold', 'Logged by']} />
              <tbody>
                {recent.map((r) => (
                  <tr key={r.id}>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                      {dowOf(r.date)} {shortDate(r.date, true)}
                    </td>
                    <td style={cell}>{r.person}</td>
                    <td style={{ ...cell, color: r.listed ? undefined : '#B71C1C' }}>{r.task}</td>
                    <td style={{ ...cell, color: r.poLine ? undefined : MUTED }}>{r.poLine || 'none'}</td>
                    <Num v={hours(r.hours)} />
                    <Num v={money(r.expenseTotal)} />
                    <Num v={money(r.sell)} />
                    <td style={{ ...cell, fontSize: 11, color: MUTED, textAlign: 'right' }}>
                      {r.source === 'import' ? 'import' : r.createdBy || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {cs.daily.length > 0 && (
        <Section
          title="Daily log (Customer Summary)"
          right={
            <button type="button" onClick={() => setDaily((v) => !v)} style={linkBtn} aria-expanded={daily}>
              {daily ? 'Hide' : `Show ${cs.daily.length} days`}
            </button>
          }
          hint="Every day's NT / OT / DT per engineer, as the Customer Summary sheet lists it."
        >
          {daily && (
            <div style={{ maxHeight: 340, overflow: 'auto' }}>
              <table aria-label="Daily grid" style={tbl}>
                <thead style={{ position: 'sticky', top: 0, background: '#fff' }}>
                  <tr>
                    {['Date', ...names, 'NT', 'OT', 'DT', 'Expenses'].map((x, i) => (
                      <th
                        key={x}
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: MUTED,
                          textTransform: 'uppercase',
                          padding: '4px 6px',
                          borderBottom: '1px solid #E5E9DD',
                          textAlign: i ? 'right' : 'left',
                        }}
                      >
                        {x}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cs.daily.map((d) => (
                    <tr key={d.date}>
                      <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                        {dowOf(d.date)} {shortDate(d.date, true)}
                      </td>
                      {names.map((nm) => {
                        const p = d.people[nm] || Object.entries(d.people).find(([k]) => k.toLowerCase() === nm.toLowerCase())?.[1];
                        return <Num key={nm} v={p ? `${hours(p.nt)}/${hours(p.ot)}/${hours(p.dt)}` : ''} />;
                      })}
                      <Num v={hours(d.nt)} />
                      <Num v={hours(d.ot)} />
                      <Num v={hours(d.dt)} />
                      <Num v={money(d.expenses)} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      )}
    </div>
  );
}
